/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit';
import { log } from '@insticator/insticator-ui'
// thunks
import { fetchAvgInViewReport, fetchGeneratedPageViews, fetchPageViewReport, fetchTopArticlesReport, fetchTotalClicksReport, fetchTotalEmbedLoadsReport, fetchTrendingNowV2Config, fetchTrendingNowV2DisplayConfig, updateTrendingNowV2Config, updateTrendingNowV2DisplayConfig } from 'state/thunks/trendingnowv2';
import { convertUTCToEST } from 'utils/date';
import { fetchProductDataLastUpdated } from 'state/thunks/product';

/* ========== ~~~~~~~~~~ TRENDING NOW V2 CONFIG SLICE : REDUCER ~~~~~~~~~~ ========== */

const initialDisplayConfig = {
    status: 'initial',
    isUpdating: false,
    config: {
        backgroundColor: "#FFFFFF",
        primaryColor: "#061240",
        enableMonochrome: false,
        useSystemFont: false,
        fontTypeface: "DECORATIVE_AND_PLAIN",
        fontSize: "SMALL"
    }
  }
const detailsDefaultData = { daily: {}, weekly: {}, monthly: {}, quarterly: {}}

const initialState = {
    lastUpdated: null,
    topArticlesByEngagement: {
        data: [],
        status: 'initial',
        isDataReceived: false
    },
    pageViewReport: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    totalClicksReport: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    avgInViewReport: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    generatedPageViewsReport: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    totalEmbedLoadsReport: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    isConfigValid: {
        generalConfig: true,
        advancedConfig: true,
        adsConfig: true
    },
    tempTrendingNowConfig: null,
    tempDisplayConfig: initialDisplayConfig,
    trendingNowConfig: {
        trendingNowId: null, 
        status: 'initial',
        generalConfig: {
            enableHeader: true,
            enableHeaderBorder: true,
            headerTitle: "Popular to the community", // 128 characters limit?
            headerIcon: "UP_TREND", // UP_TREND, THUNDER, BLACK_FIRE, FIRE, NONE
            sortConfig: {
              sortingPreference: "MOST_COMMENTS",  // MOST_COMMENTS, MOST_VIEWS, MOST_REACTIONS
              timeframe: "DAY",
              customTimeframeUnit: 0
            },
            engagementCounters: {
              showCommentsCount: false,
              showReactionsCount: false,
              showViewsCount: false
            },
            layoutConfig: {
                enableSpotlight: false,
                loadMoreStyle: "HORIZONTAL_SCROLL", // HORIZONTAL_SCROLL, BUTTON
                loadMoreUpdates: "ONE_ARTICLE" , 
                initialRowsCount: 1,
                loadMoreRowsCount: 2
            }
        },
        advancedConfig: {
          featuredArticleDetails: {
            enable: false,
            url: null,
            urlHash: null,
            setDuration: false,
            startDate: null,
            startDateEST: null, // temp FE only
            endDate: null ,
            endDateEST: null // temp FE only
          },
          articleRedirect: "COMMENTING_UNIT", // COMMENTING_UNIT, TOP
          fallbackImageType: "INSTICATOR_LOGO",  // CUSTOM, INSTICATOR_LOGO
          fallbackImageUrl: null
        },
         adsConfig: {
              mobileConfig: {
                  showBeforeUnit: true,
                  showOnFirstSlot: false,
                  showInBetweenArticles: false,
                  adsCountBetweenArticles: 3,
                  showAfterUnit: true
              },
              desktopConfig: {
                  showBeforeUnit: true,
                  showOnFirstSlot: false,
                  showInBetweenArticles: false,
                  adsCountBetweenArticles: 3,
                  showAfterUnit: true
              }
          }
    },
    displayConfig: initialDisplayConfig
};

export const trendingNowV2Slice = createSlice({
    name: 'trendingNowV2Config',
    initialState,
    reducers: {
        setTempTrendingNowConfig: (state, action) => {
            const { configName, data } = action.payload
            state.tempTrendingNowConfig[configName] = data
        },
        setTempDisplayConfig: (state, action) => {
            state.tempDisplayConfig.config = action.payload
        },
        setTrendingNowConfigSectionValid: (state, action) => {
            const { configName, isValid } = action.payload
            state.isConfigValid[configName] = isValid
        }
    },
    extraReducers: builder => {
      builder
        // fetch trending now v2 config
        .addCase(fetchTrendingNowV2Config.pending, (state, action) => {
            state.trendingNowConfig = {...initialState.trendingNowConfig, status: 'pending'}
            state.tempTrendingNowConfig = state.trendingNowConfig
        })
        .addCase(fetchTrendingNowV2Config.fulfilled, (state, action) => {
            state.trendingNowConfig = {...action.payload, status: 'fulfilled'}
            const advancedConfig = {...action.payload.advancedConfig}
            const featuredArticleDetails = {...action.payload.advancedConfig.featuredArticleDetails}
            state.trendingNowConfig = {
                ...action.payload,
                advancedConfig: {
                    ...advancedConfig,
                    featuredArticleDetails: {
                        ...featuredArticleDetails,
                        startDateEST: convertUTCToEST(featuredArticleDetails.startDate),
                        endDateEST: convertUTCToEST(featuredArticleDetails.endDate)
                    }
                },
                status: 'fulfilled'
            }
            state.tempTrendingNowConfig = state.trendingNowConfig
        })
        .addCase(fetchTrendingNowV2Config.rejected, (state, action) => {
            state.trendingNowConfig = {...initialState.trendingNowConfig, status: 'rejected'}
            state.tempTrendingNowConfig = state.trendingNowConfig
        })   

        // fetch trending now v2 display config
        .addCase(fetchTrendingNowV2DisplayConfig.pending, (state, action) => {
            state.displayConfig.status = 'pending'
            state.tempDisplayConfig = state.displayConfig
        })
        .addCase(fetchTrendingNowV2DisplayConfig.fulfilled, (state, action) => {
            if(action.payload) {
                state.displayConfig = action.payload
                state.tempDisplayConfig = action.payload
            }
            state.displayConfig.status = 'fulfilled'
        })
        .addCase(fetchTrendingNowV2DisplayConfig.rejected, (state, action) => {
            state.displayConfig.status = 'rejected'
            state.tempDisplayConfig = state.displayConfig
        })

        // update display config config
        .addCase(updateTrendingNowV2DisplayConfig.pending, (state, action) => {
            state.displayConfig.isUpdating = true
        })
        .addCase(updateTrendingNowV2DisplayConfig.fulfilled, (state, action) => {
            state.displayConfig.config = action.payload
            state.displayConfig.isUpdating = false
            state.tempDisplayConfig = state.displayConfig
        })
        .addCase(updateTrendingNowV2DisplayConfig.rejected, (state, action) => {
            state.displayConfig.isUpdating = false
        })    
        
        // update trending now config
        .addCase(updateTrendingNowV2Config.pending, (state, action) => {
        })
        .addCase(updateTrendingNowV2Config.fulfilled, (state, action) => {
            if(action.payload) {
                state.trendingNowConfig = action.payload
                state.tempTrendingNowConfig = action.payload

                const advancedConfig = {...action.payload.advancedConfig}
                const featuredArticleDetails = {...action.payload.advancedConfig.featuredArticleDetails}
                state.trendingNowConfig = {
                    ...action.payload,
                    advancedConfig: {
                        ...advancedConfig,
                        featuredArticleDetails: {
                            ...featuredArticleDetails,
                            startDateEST: convertUTCToEST(featuredArticleDetails.startDate),
                            endDateEST: convertUTCToEST(featuredArticleDetails.endDate)
                        }
                    }
                }
                state.tempTrendingNowConfig = state.trendingNowConfig
            }
        })
        .addCase(updateTrendingNowV2Config.rejected, (state, action) => {
        })    

        // trending now v2 analytics
        // fetch Avg In View Data Report
        .addCase(fetchPageViewReport.pending, (state, action) => {
            state.pageViewReport.status = 'pending'
            state.pageViewReport.details = detailsDefaultData
        })
        .addCase(fetchPageViewReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.pageViewReport.overview = Overview || state.pageViewReport.overview
            if(Daily) state.pageViewReport.details.daily = Daily;
            if(Weekly) state.pageViewReport.details.weekly = Weekly;
            if(Monthly) state.pageViewReport.details.monthly = Monthly;
            if(Quarterly) state.pageViewReport.details.quarterly = Quarterly;
            state.pageViewReport.status = 'fulfilled'
            state.pageViewReport.isDataReceived = true
        })
        .addCase(fetchPageViewReport.rejected, (state, action) => {
            state.pageViewReport.status = 'rejected'
        })

        // fetch Total Clicks Data Report
        .addCase(fetchTotalClicksReport.pending, (state, action) => {
            state.totalClicksReport.status = 'pending'
            state.totalClicksReport.details = detailsDefaultData
        })
        .addCase(fetchTotalClicksReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.totalClicksReport.overview = Overview || state.totalClicksReport.overview
            if(Daily) state.totalClicksReport.details.daily = Daily;
            if(Weekly) state.totalClicksReport.details.weekly = Weekly;
            if(Monthly) state.totalClicksReport.details.monthly = Monthly;
            if(Quarterly) state.totalClicksReport.details.quarterly = Quarterly;
            state.totalClicksReport.status = 'fulfilled'
            state.totalClicksReport.isDataReceived = true
        })
        .addCase(fetchTotalClicksReport.rejected, (state, action) => {
            state.totalClicksReport.status = 'rejected'
        })

        // fetch Top Articles Report
        .addCase(fetchTopArticlesReport.pending, (state, action) => {
            state.topArticlesByEngagement.status = 'pending'
        })
        .addCase(fetchTopArticlesReport.fulfilled, (state, action) => {
            state.topArticlesByEngagement.data= action.payload
            state.topArticlesByEngagement.status = 'fulfilled'
            state.topArticlesByEngagement.isDataReceived = true
        })
        .addCase(fetchTopArticlesReport.rejected, (state, action) => {
            state.topArticlesByEngagement.status = 'rejected'
        })
        
        // fetch Avg In View Data Report
        .addCase(fetchAvgInViewReport.pending, (state, action) => {
            state.avgInViewReport.status = 'pending'
            state.avgInViewReport.details = detailsDefaultData;
        })
        .addCase(fetchAvgInViewReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.avgInViewReport.overview = Overview || state.avgInViewReport.overview
            if(Daily) state.avgInViewReport.details.daily = Daily;
            if(Weekly) state.avgInViewReport.details.weekly = Weekly;
            if(Monthly) state.avgInViewReport.details.monthly = Monthly;
            if(Quarterly) state.avgInViewReport.details.quarterly = Quarterly;
            state.avgInViewReport.status = 'fulfilled'
            state.avgInViewReport.isDataReceived = true
        })
        .addCase(fetchAvgInViewReport.rejected, (state, action) => {
            state.avgInViewReport.status = 'rejected'
        })

        // fetch Total Embed LoadsData Report
        .addCase(fetchTotalEmbedLoadsReport.pending, (state, action) => {
            state.totalEmbedLoadsReport.status = 'pending'
            state.totalEmbedLoadsReport.details = detailsDefaultData
            state.totalEmbedLoadsReport.overview = null;
        })
        .addCase(fetchTotalEmbedLoadsReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.totalEmbedLoadsReport.overview = Overview
            state.totalEmbedLoadsReport.details = {daily: Daily, weekly: Weekly, monthly: Monthly, quarterly: Quarterly};
            state.totalEmbedLoadsReport.status = 'fulfilled'
            state.totalEmbedLoadsReport.isDataReceived = true
        })
        .addCase(fetchTotalEmbedLoadsReport.rejected, (state, action) => {
            state.totalEmbedLoadsReport.status = 'rejected'
        })

        // fetch Generated Page Views Report
        .addCase(fetchGeneratedPageViews.pending, (state, action) => {
            state.generatedPageViewsReport.status = 'pending'
            state.generatedPageViewsReport.details = detailsDefaultData
            state.generatedPageViewsReport.overview = null;
        })
        .addCase(fetchGeneratedPageViews.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.generatedPageViewsReport.overview = Overview || state.generatedPageViewsReport.overview
            if(Daily) state.generatedPageViewsReport.details.daily = Daily;
            if(Weekly) state.generatedPageViewsReport.details.weekly = Weekly;
            if(Monthly) state.generatedPageViewsReport.details.monthly = Monthly;
            if(Quarterly) state.generatedPageViewsReport.details.quarterly = Quarterly;
            state.generatedPageViewsReport.status = 'fulfilled'
            state.generatedPageViewsReport.isDataReceived = true
        })
        .addCase(fetchGeneratedPageViews.rejected, (state, action) => {
            state.generatedPageViewsReport.status = 'rejected'
        })

        // fetch product data last updated timestamp
        .addCase(fetchProductDataLastUpdated.pending, (state, action) => {
            state.lastUpdated = null;
        })
        .addCase(fetchProductDataLastUpdated.fulfilled, (state, action) => {
            const { last_updated } = action.payload
            state.lastUpdated = last_updated;
        })
        .addCase(fetchProductDataLastUpdated.rejected, (state, action) => {
            state.lastUpdated = null;
        })
    }
});

export const { setTempTrendingNowConfig, setTempDisplayConfig, setTrendingNowConfigSectionValid } = trendingNowV2Slice.actions
export default trendingNowV2Slice.reducer;
